import React, { useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import Button from "../shared/Button";
import { useReactToPrint } from "react-to-print";
import { URL } from "../../../../utils/Constants";

const ReportGeneration = ({
  subjects,
  studentList,
  assessmentName,
  yearLevelName,
  classroomName,
  studentSubjectAssessments,
  scale,
}) => {
  const componentRef = useRef();
  const printFunction = useReactToPrint({
    content: () => componentRef.current,
    orientation: "landscape",
    removeAfterPrint: true,
    documentTitle: `${assessmentName} Marks Ledger_${yearLevelName}_${classroomName}`,
  });

  //check whether is absent or not
  const isStudentAbsent = (isAbsent) => {
    if (isAbsent) {
      return true;
    }

    return false;
  };

  //convert array of students to an object to get student easily
  const studentSubjectsList = useMemo(() => {
    const map = {};

    studentSubjectAssessments.forEach((item) => {
      map[`${item.subjectId}-${item.admissionId}`] = item;
    });

    return map;
  }, [studentSubjectAssessments]);

  //determine assessment scal type and extract marks accordingly
  const checkAssessmentScaleTypeAndExtractMarks = (currentRow, scale) => {
    const theory = currentRow?.theory;
    const practical = currentRow?.practical;

    let theoryObtainedMarks, practicalObtainedMarks;

    if (scale.assessmentScaleTypeCode === "sct-mrk") {
      theoryObtainedMarks = isStudentAbsent(theory?.isAbsent)
        ? "Abs"
        : currentRow?.theory?.obtainedMarks;
      practicalObtainedMarks = isStudentAbsent(practical?.isAbsent)
        ? "Abs"
        : currentRow?.practical?.obtainedMarks;

      return { theoryObtainedMarks, practicalObtainedMarks };
    }

    if (scale.assessmentScaleTypeCode === "sct-lvl") {
      const theoryAssessmentScaleLevel = scale?.assessmentScaleLevels?.find(
        (item) => item.id === theory?.scaleLevelId,
      );
      const practicalAssessmentScaleLevel = scale?.assessmentScaleLevels?.find(
        (item) => item.id === practical?.scaleLevelId,
      );

      const theoryLevelType = theoryAssessmentScaleLevel?.levelType;
      const practicalLevelType = practicalAssessmentScaleLevel?.levelType;

      theoryObtainedMarks = isStudentAbsent(theory?.isAbsent)
        ? "Abs"
        : theoryAssessmentScaleLevel?.level;

      practicalObtainedMarks = isStudentAbsent(practical?.isAbsent)
        ? "Abs"
        : practicalAssessmentScaleLevel?.level;

      return {
        theoryObtainedMarks,
        practicalObtainedMarks,
        theoryLevelType,
        practicalLevelType,
      };
    }

    if (scale.assessmentScaleTypeCode === "sct-grd") {
      theoryObtainedMarks = isStudentAbsent(theory?.isAbsent)
        ? "Abs"
        : scale?.assessmentScaleGradePoints?.find(
            (item) => item.id === theory?.scaleGpId,
          )?.grade;

      practicalObtainedMarks = isStudentAbsent(practical?.isAbsent)
        ? "Abs"
        : scale?.assessmentScaleGradePoints?.find(
            (item) => item.id === practical?.scaleGpId,
          )?.grade;

      return { theoryObtainedMarks, practicalObtainedMarks };
    }
  };

  //extract student obtained marks
  const studentObtainedMarks = (admissionId, subjectId, scale) => {
    const currentRow = studentSubjectsList[`${subjectId}-${admissionId}`];

    const {
      theoryObtainedMarks,
      practicalObtainedMarks,
      theoryLevelType,
      practicalLevelType,
    } = checkAssessmentScaleTypeAndExtractMarks(currentRow, scale);

    return {
      theoryObtainedMarks,
      practicalObtainedMarks,
      theoryLevelType,
      practicalLevelType,
    };
  };

  return (
    <>
      <Button onClick={printFunction} className={"icon-btn"} variant="outline">
        <FontAwesomeIcon fontSize={14} icon={faPrint} />
        Print / Download
      </Button>

      <div style={{ display: "none" }}>
        <div
          ref={componentRef}
          id="printRoutine"
          className="container-fluid landscape"
        >
          <div
            className="d-flex"
            style={{
              flexDirection: "column",
              gap: "0.5rem",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <p style={{ fontSize: "1.5rem", fontWeight: "700" }}>
              {assessmentName} Marks Ledger
            </p>
            <p
              style={{
                fontSize: "1.2rem",
                fontWeight: "500",
                marginBottom: "1rem",
              }}
            >
              {yearLevelName} - {classroomName}
            </p>
          </div>

          <div className="row">
            <div
              className="col tt-widgetContent-tab-holder"
              style={{
                maxHeight: "100%",
                marginBottom: "10px",
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              <table className="table table-striped table-bordered">
                <thead
                  className="tt-group-header text-center"
                  style={{ position: "sticky", top: "-1px", zIndex: 1 }}
                >
                  <tr>
                    <th style={{ color: "black", width: "50px" }} rowSpan={2}>
                      SN
                    </th>
                    <th rowSpan={2} width="100px" style={{ color: "black" }}>
                      Student Name
                    </th>
                    {subjects.map((item) => {
                      const colSpan =
                        Number(item?.hasTheory) + Number(item?.hasPractical);
                      return (
                        <th key={item.id} colSpan={colSpan}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              flexDirection: "column-reverse",
                              gap: "0.5rem",
                            }}
                          >
                            <span style={{ color: "black" }}>{item?.name}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>

                  <tr>
                    {subjects?.map((item) => {
                      const theoryMax =
                        item?.scale?.assessmentScaleMark?.theoryMax ?? null;
                      const practicalMax =
                        item?.scale?.assessmentScaleMark?.practicalMax ?? null;

                      if (item?.hasTheory && item?.hasPractical) {
                        return (
                          <React.Fragment key={item.id}>
                            <th style={{ borderTop: "1px solid black" }}>
                              Th{theoryMax != null && `(${theoryMax})`}
                            </th>
                            <th style={{ borderTop: "1px solid black" }}>
                              Pr{practicalMax != null && `(${practicalMax})`}
                            </th>
                          </React.Fragment>
                        );
                      }

                      if (item?.hasTheory && !item?.hasPractical) {
                        return (
                          <th style={{ borderTop: "1px solid black" }}>
                            Th{theoryMax != null && `(${theoryMax})`}
                          </th>
                        );
                      }

                      if (!item?.hasTheory && item?.hasPractical) {
                        return (
                          <th style={{ borderTop: "1px solid black" }}>
                            Pr{practicalMax != null && `(${practicalMax})`}
                          </th>
                        );
                      }
                    })}
                  </tr>
                </thead>
                <tbody>
                  {studentList.length === 0 ? (
                    <>No data found</>
                  ) : (
                    studentList.map((student, idx) => (
                      <tr key={student?.admissionId}>
                        <td>{idx + 1}</td>
                        <td>
                          {student?.fullName
                            ? student.fullName
                            : "Student Name"}
                        </td>
                        {subjects.map((subject) => {
                          const studentItem = subject?.students?.find(
                            (item) => item.admissionId === student.admissionId,
                          );

                          if (!studentItem)
                            return (
                              <React.Fragment
                                key={`${student.admissionId}-${subject.id}`}
                              >
                                {subject?.hasTheory && (
                                  <td
                                    style={{ textAlign: "center" }}
                                    key={subject.id}
                                  >
                                    N/A
                                  </td>
                                )}
                                {subject?.hasPractical && (
                                  <td
                                    style={{ textAlign: "center" }}
                                    key={subject.id}
                                  >
                                    N/A
                                  </td>
                                )}
                              </React.Fragment>
                            );

                          const {
                            theoryObtainedMarks,
                            practicalObtainedMarks,
                            theoryLevelType,
                            practicalLevelType,
                          } = studentObtainedMarks(
                            student?.admissionId,
                            subject?.id,
                            scale[subject.id],
                          );

                          return (
                            <React.Fragment
                              key={`${student.admissionId}-${subject.id}`}
                            >
                              {subject.hasTheory && (
                                <td style={{ textAlign: "center" }}>
                                  {theoryLevelType &&
                                  theoryLevelType === "image" ? (
                                    ((
                                      <img
                                        src={
                                          URL.imageSelectURL +
                                          theoryObtainedMarks
                                        }
                                        style={{
                                          width: "25px",
                                          height: "25px",
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) ?? "-")
                                  ) : (
                                    <>{theoryObtainedMarks ?? "-"}</>
                                  )}
                                </td>
                              )}
                              {subject.hasPractical && (
                                <td style={{ textAlign: "center" }}>
                                  {practicalLevelType &&
                                  practicalLevelType === "image" ? (
                                    ((
                                      <img
                                        src={
                                          URL.imageSelectURL +
                                          practicalObtainedMarks
                                        }
                                        style={{
                                          width: "25px",
                                          height: "25px",
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) ?? "-")
                                  ) : (
                                    <>{practicalObtainedMarks ?? "-"}</>
                                  )}
                                </td>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportGeneration;
